/**
 * "Replacements" in UMED (2026-10-21; Isaiah Cotto: "replacements can also have their own tab in UMED"). Every hearing-aid
 * replacement request in the caller's UMED buildings: the open ones, and those placed with the Patient Advocate or
 * withdrawn in the last 30 days. GET /umed/replacements.
 *
 * Built on the board kit like "ATD — all residents": the server's tabs with their counts, a demonstrative legend drawn
 * with the same chip the rows use, synced scroll bars, and a row that opens DOCKED beside the table, never inside it.
 * Read-only except the two acts the server offers a row: withdraw (whoever filed it) and place with the Patient Advocate
 * (whoever may place). Everyone has the link to the Replacements desk, which is where those who cannot place go.
 * The server owns who is listed, the counts, the order and the page.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BoardTabs, BoardTable, BoardLegend, ColumnKey, Pager, StateMark, NotRecorded, SourceTag,
  useBoardUrlState, boardDate, boardMoney, panelId, tabId, tabMarker,
} from '../../components/board';
import type { BoardColumn, BoardTabDef } from '../../components/board';
import { StateBlock } from '../../components/tide';
import { replacementApi, etDay, REPLACEMENT_TABS, SIDE_OPTIONS } from '../../components/replacements/replacementRequestsApi';
import type { ReplacementRow, ReplacementsPage } from '../../components/replacements/replacementRequestsApi';

const DEFAULT_TAB = 'open';

const sideWord = (s: string) => SIDE_OPTIONS.find((o) => o.value === s)?.label || s;
const PATH_WORDS: Record<string, string> = {
  REVIEW_REQUIRED: 'Review required', WARRANTY: 'Warranty', REPLACEMENT_CHARGE: 'Replacement charge', NEW_UMED: 'New UMED',
  PRIVATE_PAY: 'Private pay', SPENDDOWN_PLUS_UMED: 'Spenddown plus UMED',
};
const REASON_WORDS: Record<string, string> = {
  LOST: 'Lost', DAMAGED: 'Damaged', NOT_WORKING: 'Not working', RECHARGEABLE_UPGRADE: 'Rechargeable upgrade',
  WARRANTY_REPLACEMENT: 'Warranty replacement', OTHER: 'Other',
};
/** The reason alone, from its code (the side, path and amount have their own places). */
const reasonOf = (r: ReplacementRow): string => REASON_WORDS[r.reason_code] || r.reason_code;
/** What still stands in the way of placing, in plain words (replacementQueue.readiness). */
const MISSING_WORDS: Record<string, string> = {
  WARRANTY_NOT_CHECKED: 'the warranty has not been checked',
  OUT_OF_WARRANTY: 'the warranty check failed',
  WARRANTY_ENDED: 'the warranty has ended',
  PAID_IN_FULL_NOT_CHECKED: 'paid in full has not been checked',
  NOT_PAID_IN_FULL: 'the paid-in-full check failed',
};
const missingWords = (codes: string[]): string => codes.map((c) => MISSING_WORDS[c] || c).join('; ');

/** The state chip a row draws; the legend draws the same component for each tab. */
function StateChip({ r, tabs }: { r: ReplacementRow; tabs: BoardTabDef[] }) {
  const t = tabs.find((x) => x.key === r.state.toLowerCase()) || null;
  return <StateMark word={r.state_words} color={t ? t.color : null} marker={t ? tabMarker(t) : 'hatch'} stateKey={r.state.toLowerCase()} />;
}

function PaCell({ r }: { r: ReplacementRow }) {
  if (r.state === 'PLACED') return r.pa_assigned ? <>{r.pa_assigned}<SourceTag source="EMERGE" /></> : <NotRecorded label="PA not recorded" />;
  if (r.state === 'OPEN') return r.goes_to ? <>Goes to {r.goes_to} when placed<SourceTag source="EMERGE" /></> : <NotRecorded label="no PA covers this building" />;
  return <span className="board-na">not placed</span>;
}

function columns(tabs: BoardTabDef[]): BoardColumn<ReplacementRow>[] {
  return [
    {
      key: 'facility', label: 'Building', source: 'EMERGE',
      help: 'The resident\'s building in eMerge now: the building the Replacements desk lists the request under, and whose Patient Advocate it is placed with.',
      render: (r) => (r.facility
        ? <><span className="board-clip" title={r.facility.name || undefined}>{r.facility.name || 'Name not recorded'}</span><SourceTag source="EMERGE" detail="building" /></>
        : <NotRecorded label="no building on file" />),
    },
    { key: 'side', label: 'Side', source: 'EMERGE', help: 'Which ear: left, right or both.', render: (r) => sideWord(r.side) },
    { key: 'reason', label: 'Reason', source: 'EMERGE', help: 'Why a replacement was asked for. The note, if any, is in the open record.', render: (r) => reasonOf(r) },
    {
      key: 'requested_by', label: 'Requested by', source: 'EMERGE', help: 'The office member who filed the request.',
      render: (r) => (r.requested_by ? <>{r.requested_by}{r.requested_by_me ? ' (you)' : ''}</> : <NotRecorded />),
    },
    {
      key: 'requested_on', label: 'Requested on', source: 'EMERGE', help: 'The day the request was filed (New York time).',
      render: (r) => etDay(r.requested_at) || <NotRecorded />,
    },
    {
      key: 'state', label: 'State', source: 'EMERGE',
      help: 'Requested: waiting on the Replacements desk. Placed to PA: a place-capable person placed it with the facility\'s Patient Advocate. Cancelled: the person who filed it withdrew it. The shape and colour are the legend\'s.',
      render: (r) => <StateChip r={r} tabs={tabs} />,
    },
    {
      key: 'pa', label: 'PA assigned', source: 'EMERGE',
      help: 'Placed: the Patient Advocate who holds the placed case. Requested: the Patient Advocate who covers the building, who it goes to when placed.',
      render: (r) => <PaCell r={r} />,
    },
  ];
}

interface LoadError { message: string; status?: number; code?: string }

function outcome(r: { status?: number; code?: string; message?: string; error?: string }): string {
  if (r.status === undefined) return 'Could not reach the server, so nothing here is current.';
  return r.message || r.error || 'The list could not be loaded.';
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return <p className="board-basis" style={{ padding: '2px 16px' }}><strong>{label}:</strong> {children}</p>;
}

function CheckLine({ r }: { r: ReplacementRow }) {
  if (r.state !== 'OPEN') return null;
  const c = r.checks;
  const warranty = c && c.warranty ? `${c.warranty.result === 'PASS' ? 'passed' : 'failed'}${c.warranty.warranty_expires_on ? ` (warranty to ${boardDate(c.warranty.warranty_expires_on)})` : ''}${c.warranty.checked_by ? `, checked by ${c.warranty.checked_by}` : ''}` : 'not checked yet';
  const paid = c && c.paid_in_full ? `${c.paid_in_full.result === 'PASS' ? 'passed' : 'failed'}${c.paid_in_full.checked_by ? `, checked by ${c.paid_in_full.checked_by}` : ''}` : 'not checked yet';
  return (
    <>
      <Fact label="Warranty check">{warranty}</Fact>
      <Fact label="Paid-in-full check">{paid}</Fact>
      <Fact label="Can it be placed today">
        {r.ready ? 'Ready to place' : `Not yet${r.missing && r.missing.length ? `: ${missingWords(r.missing)}` : ''}`}
      </Fact>
    </>
  );
}

function ReplacementDetail({ r, deskHref, onChanged }: { r: ReplacementRow; deskHref: string; onChanged: (r: ReplacementRow, msg: string) => void }) {
  const [mode, setMode] = useState<'idle' | 'withdraw' | 'place'>('idle');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const withdraw = async (e: React.FormEvent) => {
    e.preventDefault();
    const reason = text.trim();
    if (busy || reason.length < 3 || reason.length > 500) return;
    setBusy(true); setError('');
    const res = await replacementApi.cancel(r.id, reason);
    if (res.success) { setBusy(false); setMode('idle'); setText(''); onChanged(r, 'Your request was withdrawn.'); return; }
    setError(res.status === undefined && !res.code ? 'eMerge did not confirm the withdrawal. The request is still open. Try again.' : (res.message || res.error || 'The request was not withdrawn.'));
    setBusy(false);
  };
  const place = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    const res = await replacementApi.place(r.patient_id, text.trim() || undefined);
    if (res.success) { setBusy(false); setMode('idle'); setText(''); onChanged(r, 'Placed with the Patient Advocate.'); return; }
    const missing = Array.isArray(res.missing) ? res.missing : (res.detail && Array.isArray((res.detail as any).missing) ? (res.detail as any).missing : []);
    const base = res.status === undefined && !res.code ? 'eMerge did not confirm the placement. Nothing is shown as placed. Check the row before trying again.' : (res.message || res.error || 'The replacement was not placed.');
    setError(missing.length ? `${base} Still needed: ${missingWords(missing)}.` : base);
    setBusy(false);
  };

  return (
    <div className="um-dock">
      <p className="board-basis" style={{ padding: '8px 16px 0' }}>
        {r.resident || 'Resident name not recorded'} · {r.facility ? r.facility.name || 'Building name not recorded' : 'No building on file'}
      </p>
      <Fact label="What was asked for">{r.reason_words}</Fact>
      <Fact label="Note">{r.note ? r.note : <NotRecorded label="no note" />}</Fact>
      <Fact label="How it is paid for">{PATH_WORDS[r.financial_path] || r.financial_path}{r.requested_amount_cents !== null && r.requested_amount_cents !== undefined ? `, ${boardMoney(r.requested_amount_cents / 100)}` : ''}</Fact>
      <Fact label="Requested">{etDay(r.requested_at) || 'date not recorded'}{r.requested_by ? ` by ${r.requested_by}` : ''}{r.requested_by_me ? ' (you)' : ''}</Fact>
      <Fact label="State">{r.state_words}</Fact>
      {r.state === 'PLACED' && <Fact label="Placed">{etDay(r.placed_at) || 'date not recorded'}{r.placed_by ? ` by ${r.placed_by}` : ''}{r.pa_assigned ? `; the PA is ${r.pa_assigned}` : ''}</Fact>}
      {r.state === 'CANCELLED' && <Fact label="Withdrawn">{etDay(r.cancelled_at) || 'date not recorded'}{r.cancelled_by ? ` by ${r.cancelled_by}` : ''}{r.cancel_reason ? `. Why: ${r.cancel_reason}` : ''}</Fact>}
      {r.state === 'OPEN' && <Fact label="Goes to">{r.goes_to ? `${r.goes_to} when placed` : 'no PA covers this building'}</Fact>}
      <CheckLine r={r} />

      <div className="um-section">
        {error && <p className="um-notice stop" role="alert">{error}</p>}
        {mode === 'idle' && (r.can_cancel || r.can_place) && (
          <div>
            {r.can_cancel && <button type="button" className="um-open" onClick={() => { setMode('withdraw'); setText(''); setError(''); }}>Withdraw my request</button>}{' '}
            {r.can_place && <button type="button" className="um-open um-primary" onClick={() => { setMode('place'); setText(''); setError(''); }}>Place with the PA</button>}
          </div>
        )}
        {mode === 'withdraw' && (
          <form onSubmit={withdraw} aria-label="Withdraw my replacement request">
            <label className="um-rowfield"><span>Why are you withdrawing it? (3 to 500 characters)</span>
              <input value={text} maxLength={500} disabled={busy} onChange={(e) => setText(e.target.value)} aria-label="Why are you withdrawing it?" />
            </label>
            <button type="submit" className="um-open um-primary" disabled={busy || text.trim().length < 3}>{busy ? 'Withdrawing…' : 'Withdraw request'}</button>{' '}
            <button type="button" className="um-link" disabled={busy} onClick={() => { setMode('idle'); setError(''); }}>Keep it</button>
          </form>
        )}
        {mode === 'place' && (
          <form onSubmit={place} aria-label="Place the replacement with the Patient Advocate">
            <label className="um-rowfield"><span>Note for the Patient Advocate (optional)</span>
              <input value={text} maxLength={1000} disabled={busy} onChange={(e) => setText(e.target.value)} aria-label="Note for the Patient Advocate" />
            </label>
            <button type="submit" className="um-open um-primary" disabled={busy}>{busy ? 'Placing…' : 'Place now'}</button>{' '}
            <button type="button" className="um-link" disabled={busy} onClick={() => { setMode('idle'); setError(''); }}>Not now</button>
          </form>
        )}
        <p><a href={deskHref}>Open on the Replacements desk</a></p>
      </div>
    </div>
  );
}

export interface UmedReplacementsBoardProps {
  /** Prefix for the tab ids (one board per page). */
  idBase?: string;
  /** Called after an act the server confirmed, so the sidebar badge can be read again. */
  onChanged?: () => void;
}

export default function UmedReplacementsBoard({ idBase = 'um-replacements', onChanged }: UmedReplacementsBoardProps) {
  const [st, update] = useBoardUrlState([], DEFAULT_TAB);
  const status = (REPLACEMENT_TABS as readonly string[]).includes(st.tab) ? st.tab : DEFAULT_TAB;
  const [data, setData] = useState<ReplacementsPage | null>(null);
  const [lastTabs, setLastTabs] = useState<BoardTabDef[]>([]);
  const [err, setErr] = useState<LoadError | null>(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(st.q);
  const [changed, setChanged] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => { setDraft(st.q); }, [st.q]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true); setErr(null);
    const r = await replacementApi.list({ state: status, q: st.q, page: st.page });
    if (mine !== seq.current) return;
    if (r.success && r.data) { setData(r.data); setLastTabs(r.data.tabs); }
    else { setData(null); setErr({ message: outcome(r), status: r.status, code: r.code }); }
    setLoading(false);
  }, [status, st.q, st.page]);
  useEffect(() => { load(); }, [load]);
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => { setChanged(null); }, [status, st.q, st.page]);

  const onRowChanged = useCallback((row: ReplacementRow, msg: string) => {
    setChanged(`${row.resident || 'The resident'}: ${msg}`);
    loadRef.current();
    if (onChanged) onChanged();
  }, [onChanged]);

  const tabs: BoardTabDef[] = data ? data.tabs : lastTabs.map((t) => ({ ...t, count: undefined }));
  const activeTab = tabs.find((t) => t.key === status) || null;
  const cols = useMemo(() => columns(tabs), [tabs]);
  const filtered = Boolean(st.q);
  const clear = () => { setDraft(''); update({ q: '' }); };
  const noun = (n: number) => (n === 1 ? 'request' : 'requests');

  const identity = {
    label: 'Resident', sort: undefined as string | undefined,
    help: 'The resident the replacement was asked for. Open a row to see the note, the checks and the acts you may make; it opens beside the table.',
    render: (r: ReplacementRow) => (
      <>
        <strong>{r.resident || 'Name not recorded'}</strong>
        <span className="board-src">{r.facility ? r.facility.name || 'building name not recorded' : 'no building on file'}</span>
      </>
    ),
    name: (r: ReplacementRow) => r.resident || 'resident',
  };

  return (
    <section className="board board-frame" aria-label="Replacements">
      {tabs.length > 0 && <BoardTabs tabs={tabs} value={status} onChange={(k) => update({ tab: k })} label="Replacement state" idBase={idBase} />}
      <div role="tabpanel" id={panelId(idBase)} aria-labelledby={activeTab ? tabId(idBase, activeTab.key) : undefined} className="board-panel">
        <div className="board-head">
          {activeTab && <p className="board-what">{activeTab.what}<span className="board-what__src">eMerge replacement requests · UMED</span></p>}
          <p className="board-basis">
            {data ? data.rule : 'Open replacement requests, plus those placed or withdrawn in the last 30 days, in your UMED buildings.'}
            {' '}Placing a replacement with the Patient Advocate is done by those who place replacements; everyone can follow it on the Replacements desk.
          </p>
          {changed && <p className="board-caveat" role="status">{changed}</p>}
          <div className="board-controls">
            <form className="board-search" role="search" onSubmit={(e) => { e.preventDefault(); update({ q: draft.trim() }); }}>
              <label className="board-field"><span>Resident or building</span>
                <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Name or building" aria-label="Search replacement requests" maxLength={120} />
              </label>
              <button type="submit" className="board-btn">Find</button>
            </form>
            {filtered && <button type="button" className="board-btn" onClick={clear}>Clear search</button>}
            {data && (
              <span className="board-result" aria-live="polite">
                <strong>{data.total.toLocaleString('en-US')}</strong> {noun(data.total)} in {activeTab?.label || 'this view'}{filtered ? ' matching your search' : ''}{loading ? ' · updating…' : ''}
              </span>
            )}
          </div>
          {data && (
            <BoardLegend
              title="Replacement state"
              tabs={tabs}
              allKey="all"
              units={['request', 'requests']}
              value={status}
              onSelect={(k) => update({ tab: k })}
              scope="Every request is in exactly one of these three, shown by a word, a shape and a colour; the counts follow your search. Choose one to list only those requests."
            />
          )}
        </div>

        {!data && loading && !err && <div className="board-state"><StateBlock kind="loading" shape="table" label="Loading replacement requests…" /></div>}
        {err && <div className="board-state"><StateBlock kind="error" status={err.status} message={err.message} onRetry={load} /></div>}
        {data && data.items.length === 0 && data.total > 0 && (
          <div className="board-state">
            <StateBlock kind="empty" reason="filtered" message={`Page ${st.page} is past the end: this view has ${data.total.toLocaleString('en-US')} ${noun(data.total)}.`} onClear={() => update({ page: 1 })} clearLabel="Go to page 1" />
          </div>
        )}
        {data && data.items.length === 0 && data.total === 0 && (
          <div className="board-state">
            {filtered
              ? <StateBlock kind="empty" reason="filtered" message={`No replacement request in ${activeTab?.label || 'this view'} matches your search.`} onClear={clear} clearLabel="Clear search" />
              : <StateBlock kind="empty" reason="none" message={`No replacement request is in ${activeTab?.label || 'this view'} right now.`} />}
          </div>
        )}
        {data && data.items.length > 0 && (
          <BoardTable<ReplacementRow>
            label={`Replacements, ${activeTab?.label || status}, ${data.total} ${noun(data.total)}`}
            columns={cols}
            rows={data.items}
            rowKey={(r) => r.id}
            identity={identity}
            openKey={st.open}
            onToggle={(k) => update({ open: st.open === k ? null : k })}
            detailLabel={(r) => `${r.resident || 'Resident'}: replacement`}
            renderDetail={(r) => <ReplacementDetail key={r.id} r={r} deskHref={data.desk_href} onChanged={onRowChanged} />}
            sort={data.sort} dir={data.dir} sorts={data.sorts}
            onSort={(col, dir) => update({ sort: col, dir })}
            busy={loading}
          />
        )}
        {data && <Pager page={data.page} limit={data.limit} total={data.total} onPage={(p) => update({ page: p })} label="requests" />}
      </div>
      <div className="board-foot">
        {data && activeTab && <p>Counted and listed by one predicate ({activeTab.predicate}); the count on the tab is the number of rows here. &ldquo;not recorded&rdquo; means eMerge holds no value.</p>}
        <ColumnKey columns={cols} identity={identity} />
      </div>
    </section>
  );
}
